import { useContext } from 'react';
import { useDispatch } from 'react-redux';

import url from '../helpers/url';
import { COMPONENT_TYPES, ITEM_TYPE, PROGRAM_TYPE, KIDSMODE } from '../config/constants';
import { __ } from '../translations';
import h, { getNewContentRating } from '../helpers/common';
import user from '../helpers/user';
import viewEvents from '../helpers/viewEvents';
import subscription from '../helpers/subscription';
import { getItemProperties, getMixpanelVodProperties, getMixpanelLinearProperties } from '../contexts/MixpanelHelper';
import { EVENT, KEYS } from '../contexts/MixpanelConstants';
import { MixpanelContext } from '../contexts/MixpanelContext';
import { BRAZE } from '../config/analytics-events';
import useBraze from './useBraze';
import { Button } from '@intigral/shared/components/Button';
import { Text } from '@intigral/shared/components/Text';
import { setSession } from '@intigral/utils/lib/browser-storage/localStorage';
import { showUpgradedModal } from '../store/reducers/generalConfigurationSlice';
import { useConfigurationSections } from './useConfiguration';
import playerService from '../helpers/bitmovinPlayer';
import usePPVManagement from './usePPVManagement';
import { showGuestPromotionPopup } from '../config/guestMode';

interface UsePlayVideoClickOptions {
    buttonType?: string;
    componentType?: string;
    componentName?: string;
    meta?: any;
    testId?: string;
    onClick?: () => void;
    shouldCallMixPanelEvent?: boolean;
}

const canProfilePlay = (item: any) => {
    const itemRating: string = item?.ratings?.toUpperCase();
    const currentProfileRating: string = user.getProfile()?.newParentalControl?.toUpperCase();

    if (!itemRating || !currentProfileRating) {
        return false;
    }
    // An array of rating, like this: ["G", "PG", "PG12", "PG15", "R15", "R18"]
    const contentRatingArray = getNewContentRating();
    // If the current profile rating has higher/equal index then it can play the content.
    return contentRatingArray.indexOf(currentProfileRating) >= contentRatingArray.indexOf(itemRating);
};

/**
 * Play-click side effects shared by every play entry point: guest redirect, analytics, and the
 * subscription / PPV / parental-rating checks. Returns `false` when playback must not start; in
 * that case the event's default is prevented so a wrapping `Link` does not navigate either.
 */
const usePlayVideoClick = ({
    buttonType,
    componentType,
    componentName,
    meta,
    testId,
    onClick,
    shouldCallMixPanelEvent = true,
}: UsePlayVideoClickOptions = {}) => {
    const dispatch = useDispatch();
    const { logBrazeCustomEvent } = useBraze();
    const { track }: { track: any } = useContext(MixpanelContext);
    const { isPPVItem: checkIsPPVItem, getPPVStatus } = usePPVManagement();
    const { paidVsFree } = useConfigurationSections();
    const enableLockIcon = paidVsFree?.lockIconEnable || false;

    const renderMessage = ({ msgTitle, msgText, msgButtonText, clickURL, onCTAHandler }) => {
        // eslint-disable-next-line
        // @ts-expect-error
        if (typeof $zopim !== 'undefined' && typeof $zopim.livechat !== 'undefined') {
            // eslint-disable-next-line
            // @ts-expect-error
            $zopim.livechat.button.show();
        }
        return (
            <>
                <Text as="h2" variant="h2" margin={{ default: '2x', md: '3x' }}>
                    {msgTitle}
                </Text>
                <Text as="p" alignment="center" color="gray" margin={{ default: '2x', md: '4x' }}>
                    {msgText}
                </Text>
                {clickURL && msgButtonText && user.getProfileMode() !== KIDSMODE.KID && (
                    <Button
                        primary
                        style={{ width: '100%' }}
                        LinkComponent="a"
                        data-test-id={testId}
                        href={clickURL}
                        onClick={() => {
                            onCTAHandler();
                        }}
                    >
                        {msgButtonText}
                    </Button>
                )}
            </>
        );
    };

    const getPopupMsg = (msgTitle, msgText, msgButtonText?, clickURL?) => {
        const onMount = () => {
            track(EVENT.OVERLAY.ADDON_SUBSCRIPTION.POPUP_VIEW, {
                'Addon Name': msgTitle,
            });
        };

        const onCTAHandler = () => {
            track(EVENT.OVERLAY.ADDON_SUBSCRIPTION.POPUP_SUBSCRIBE, {
                'Addon Name': msgTitle,
            });
        };

        const onUnmount = () => {
            track(EVENT.OVERLAY.ADDON_SUBSCRIPTION.POPUP_DISMISS, {
                'Addon Name': msgTitle,
            });
        };

        const messageComponent = renderMessage({
            msgTitle,
            msgText,
            msgButtonText,
            clickURL,
            onCTAHandler,
        });
        viewEvents.broadcast('openPopup', {
            component: messageComponent,
            onMount,
            onUnmount,
        });
    };

    const handleMixpanelEvents = (item) => {
        const eventProps = {
            [KEYS.CAROUSEL.SCREEN_NAME]: h.getScreen(),
            [KEYS.CAROUSEL.CAROUSEL_NAME]: componentName,
            [KEYS.CAROUSEL.CAROUSEL_TYPE]: h.getCarouselComponentType(componentType),
        };
        switch (componentType) {
            case COMPONENT_TYPES.FEATURED_SLIDER:
                track(`${h.getScreen()} - ${EVENT.PROMOTIONS.PLAY}`, {
                    ...getItemProperties(item),
                    ...eventProps,
                    'Promotion Type': item.promoType,
                    'Promotion Title': item.title || item?.programTitle?.en || '',
                });
                break;
            case COMPONENT_TYPES.VOD_CAROUSEL:
                if (componentName === COMPONENT_TYPES.SEARCH_RECOMMENDATIONS) {
                    track(`${h.getScreen()} - ${EVENT.SEARCH_RESULTS.RECOMMENDATION_ITEM_PLAY}`, {
                        [KEYS.GENERAL.SEARCH_RECOMMENDATION_TYPE]: meta.title.en,
                        ...getMixpanelVodProperties(item),
                        ...eventProps,
                    });
                } else {
                    track(`${h.getScreen()} - ${EVENT.CAROUSEL.ITEM_PLAY}`, {
                        [KEYS.GENERAL.CAROUSLE_NAME]: componentName,
                        ...getMixpanelVodProperties(item),
                        ...eventProps,
                    });
                }
                break;
            case COMPONENT_TYPES.VOD_FEATURED_CAROUSEL:
                track(`${h.getScreen()} - ${EVENT.CAROUSEL.ITEM_PLAY}`, {
                    [KEYS.GENERAL.CAROUSLE_NAME]: componentName,
                    ...getMixpanelVodProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.USER_WATCHLIST:
                track(`${h.getScreen()} - ${EVENT.WATCHLIST.ITEM_PLAY}`, {
                    [KEYS.GENERAL.ITEM_TYPE]: item.category,
                    ...getItemProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.REWIND_CAROUSEL:
                track(`${h.getScreen()} - ${EVENT.REWIND.ITEM_CLICK}`, {
                    ...getMixpanelLinearProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.SEARCH_RESULTS:
                track(`${h.getScreen()} - ${EVENT.SEARCH_RESULTS.ITEM_PLAY}`, {
                    ...(item.programType && {
                        [KEYS.GENERAL.ITEM_TYPE]: ITEM_TYPE[item.programType.toUpperCase()],
                    }),
                    ...getItemProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.DETAILS_CONTAINER:
                track(`${h.getScreen()} - ${EVENT.VOD_DETAILS.WATCH_CLICK}`, {
                    ...getMixpanelVodProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.SEASON_EPISODES:
                track(`${h.getScreen()} - ${EVENT.VOD_DETAILS.EPISODE_PLAY_CLICK}`, {
                    ...getMixpanelVodProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.FULLVOD_CATALOG_LIST:
                track(`${h.getScreen()} - ${EVENT.VOD_LISTING.ITEM_PLAY}`, {
                    ...getMixpanelVodProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.CONTINIE_WATCHING:
                track(`${h.getScreen()} - ${EVENT.CONTINUE_WATCHING.ITEM_PLAY}`, {
                    [KEYS.GENERAL.PROGRAM_TYPE]: item.programType,
                    ...(item.programType === 'VOD'
                        ? getMixpanelVodProperties(item)
                        : getMixpanelLinearProperties(item)),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.WATCHLIST_CAROUSEL:
                track(`${h.getScreen()} - ${EVENT.WATCHLIST_CAROUSEL.ITEM_PLAY}`, {
                    [KEYS.GENERAL.WATCHLIST_TYPE]: item.category,
                    ...getItemProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.TV_GUIDE_CAROUSEL:
                track(`${h.getScreen()} - ${EVENT.TV_GUIDE_CAROUSEL.PROGRAM_CLICK}`, {
                    ...getMixpanelLinearProperties(item),
                    ...eventProps,
                });
                break;
            default:
                break;
        }
    };

    const checkUserAllowedToPlay = (e, item): boolean => {
        if (item && Object.keys(item).length > 0 && !h.hasTVODTag(item)) {
            // eslint-disable-next-line
            const vodItemPackages = item.hasOwnProperty('subscriptionGuids') ? item.subscriptionGuids : null;

            const subscriptionGuid = item?.subscriptionGuids?.[0];

            if ('isLocked' in item && item.isLocked) {
                e.preventDefault();
                if (user.getUserSegment() === 'B2B') {
                    getPopupMsg(
                        subscriptionGuid
                            ? __(`b2bNotSubscribed_popup_header_${subscriptionGuid}`)
                            : __('b2bNotSubscribed_popup_header'),
                        subscriptionGuid
                            ? __(`b2bNotSubscribed_popup_description_${subscriptionGuid}`)
                            : __('b2bNotSubscribed_popup_description')
                    );
                } else {
                    getPopupMsg(
                        __(
                            subscriptionGuid
                                ? `loggedInNoExtraAddon_popup_header_${subscriptionGuid}`
                                : 'loggedInNoExtraAddon_popup_header'
                        ),
                        __(
                            subscriptionGuid
                                ? `loggedInNoExtraAddon_popup_description_${subscriptionGuid}`
                                : 'loggedInNoExtraAddon_popup_description'
                        ),
                        __('loggedInNoExtraAddon_popup_button_title'),
                        user.getUserProfileUrl()
                    );
                }
                return false;
            }

            // Check if this is a PPV item using the hook
            const isPPVItemResult = checkIsPPVItem(item, buttonType);

            // For PPV items, we need special handling
            if (isPPVItemResult) {
                const channelId =
                    item?.channelId || item?.channelID || item?.id || item?.channelNo || item?.channelNumber;

                // Get comprehensive PPV status using the hook
                const { isLocked: shouldShowLock } = getPPVStatus(item, channelId);

                if (shouldShowLock) {
                    // PPV not purchased, show upgrade modal
                    e.preventDefault();
                    if (enableLockIcon) {
                        dispatch(showUpgradedModal(true));
                    } else {
                        getPopupMsg(
                            __('loggedInUnSub_popup_header'),
                            __('loggedInUnSub_popup_description1'),
                            __('loggedInUnSub_popup_actionBtn_title'),
                            user.getUserProfileUrl()
                        );
                    }
                    return false;
                }
                // PPV is purchased, allow navigation
                return true;
            }

            if (!user.isDefaultUser() && !subscription.checkItemBelongsToActivePackages(vodItemPackages)) {
                e.preventDefault();
                if (enableLockIcon) {
                    dispatch(showUpgradedModal(true));
                } else {
                    getPopupMsg(
                        __('loggedInUnSub_popup_header'),
                        __('loggedInUnSub_popup_description1'),
                        __('loggedInUnSub_popup_actionBtn_title'),
                        user.getUserProfileUrl()
                    );
                }
                return false;
            }

            if (!canProfilePlay(item) && h.isDeviceSupported()) {
                e.preventDefault();
                // TODO : adding delay to overcome hiding parental popup behind detail overlay
                setTimeout(() => {
                    getPopupMsg(
                        __('livetv_content_not_allowed_title'),
                        __('livetv_content_not_allowed_desc'),
                        __('parental_control_popup_btn_text'),
                        url.route('profile-settings')
                    );
                }, 100);
                return false;
            }
        }
        return true;
    };

    const handleBrazeEvents = (item) => {
        const vodTitle = item?.name || item?.textEn || item?.text || item?.title || item?.programTitle?.en || '';
        const tvShowName = item?.seriesTitle || vodTitle;
        const vodType = item?.programType ? ITEM_TYPE[item.programType.toUpperCase()] ?? item.programType : '';
        const vodCategory = item?.categories
            ? Array.isArray(item.categories)
                ? item.categories.join(',')
                : String(item.categories)
            : item?.category ?? '';
        const language = item?.language
            ? Array.isArray(item.language)
                ? item.language.join(',')
                : String(item.language)
            : '';
        const seasonName = String(url.getQuery('season') || item?.tvSeasonNumber || item?.seasonNumber || '');

        if (item.programType === PROGRAM_TYPE.SERIES) {
            logBrazeCustomEvent(BRAZE.EVENTS.VOD_WATCHED, {
                [BRAZE.PROPERTIES.VOD_TYPE]: vodType,
                [BRAZE.PROPERTIES.VOD_CATEGORY]: vodCategory,
                [BRAZE.PROPERTIES.TV_SHOW_NAME]: tvShowName,
                [BRAZE.PROPERTIES.SEASON_NAME]: seasonName,
                [BRAZE.PROPERTIES.GENRE]: item?.genre ?? '',
                [BRAZE.PROPERTIES.LANGUAGE]: language,
            });
        } else if (item.programType === PROGRAM_TYPE.EPISODE) {
            logBrazeCustomEvent(BRAZE.EVENTS.VOD_WATCHED, {
                [BRAZE.PROPERTIES.VOD_TYPE]: vodType,
                [BRAZE.PROPERTIES.VOD_CATEGORY]: vodCategory,
                [BRAZE.PROPERTIES.TV_SHOW_NAME]: tvShowName,
                [BRAZE.PROPERTIES.EPISODE_NAME]: item?.tvSeasonEpisodeNumber ?? '',
                [BRAZE.PROPERTIES.SEASON_NAME]: seasonName,
                [BRAZE.PROPERTIES.GENRE]: item?.genre ?? '',
                [BRAZE.PROPERTIES.LANGUAGE]: language,
            });
        } else {
            logBrazeCustomEvent(BRAZE.EVENTS.VOD_WATCHED, {
                [BRAZE.PROPERTIES.VOD_TYPE]: vodType,
                [BRAZE.PROPERTIES.VOD_CATEGORY]: vodCategory,
                [BRAZE.PROPERTIES.MOVIE_NAME]: vodTitle,
                [BRAZE.PROPERTIES.GENRE]: item?.genre ?? '',
                [BRAZE.PROPERTIES.LANGUAGE]: language,
            });
        }
    };

    const handlePlayClick = (e, item): boolean => {
        e.stopPropagation();
        if (showGuestPromotionPopup(e)) {
            return false;
        }

        const itemIsLiveTV = item?.category === 'LINEAR' || item?.promoType === 'LIVESTREAM' || false;
        if (itemIsLiveTV) {
            setSession('noAds', 'true');
        }
        if (item?.useCaseId) {
            playerService.setUseCaseId(item?.useCaseId);
        }
        onClick?.();
        if (shouldCallMixPanelEvent) {
            handleMixpanelEvents(item);
        }
        handleBrazeEvents(item);

        const excludedType = ['CHANNEL', 'LIVESTREAM', 'PPV', 'ROUTE'];
        const { promoType } = item;
        if (typeof promoType === 'undefined' || !excludedType.includes(promoType)) {
            return checkUserAllowedToPlay(e, item);
        }
        return true;
    };

    return handlePlayClick;
};

export default usePlayVideoClick;
